import { DerbyRngV1 } from '../rng';
import { DRILL_TICK_S, steerOf, type MinigameDef, type MinigameSession, type SessionScore } from './framework';

export interface PoleTarget { tick: number; side: -1 | 1; cleared: boolean | null }

/** Speed slalom: get to the called side of every pole without scrubbing pace. */
export class PolesSession implements MinigameSession {
  readonly durationTicks = Math.round(15 / DRILL_TICK_S);
  readonly poles: PoleTarget[];
  tick = 0;
  p = 0;
  combo = 0;
  bestCombo = 0;
  misses = 0;
  private nextPole = 0;

  constructor(readonly tier: number, seed: number) {
    const rng = new DerbyRngV1(seed).fork('poles');
    const intervalS = 1.45 - 0.12 * Math.max(0, Math.min(3, tier - 1));
    const poles: PoleTarget[] = [];
    let at = Math.round(1.4 / DRILL_TICK_S);
    let side: -1 | 1 = rng.chance(0.5) ? -1 : 1;
    while (at < this.durationTicks - 10) {
      poles.push({ tick: at, side, cleared: null });
      side = -side as -1 | 1;
      at += Math.round(rng.range(intervalS * 0.88, intervalS * 1.12) / DRILL_TICK_S);
    }
    this.poles = poles;
  }

  get finished(): boolean { return this.tick >= this.durationTicks; }
  nextTarget(): PoleTarget | null { return this.poles[this.nextPole] ?? null; }

  step(mask: number): void {
    if (this.finished) return;
    const steer = steerOf(mask);
    this.p += steer * (1.45 + this.tier * 0.05) * DRILL_TICK_S;
    if (steer === 0) this.p -= Math.sign(this.p) * Math.min(Math.abs(this.p), 0.32 * DRILL_TICK_S);
    this.p = Math.max(-1, Math.min(1, this.p));
    const target = this.poles[this.nextPole];
    if (target && this.tick >= target.tick) {
      const cleared = this.p * target.side >= 0.28;
      target.cleared = cleared;
      if (cleared) {
        this.combo += 1;
        this.bestCombo = Math.max(this.bestCombo, this.combo);
      } else {
        this.combo = 0;
        this.misses += 1;
      }
      this.nextPole += 1;
    }
    this.tick += 1;
  }

  score(): SessionScore {
    const cleared = this.poles.filter((p) => p.cleared).length;
    const raw = this.poles.length ? Math.max(0, Math.min(1, cleared / this.poles.length + 0.02 * this.bestCombo)) : 0;
    return { raw, detail: `${cleared}/${this.poles.length} poles · combo ×${this.bestCombo}`, failed: false };
  }
}

export const POLES_DEF: MinigameDef = {
  id: 'poles', name: 'Pole Bending', stat: 'speed', energyCost: 4,
  create: (tier, seed) => new PolesSession(tier, seed),
};
